import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@devhop/ui";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import type {
  FormLotteryPrizeDTO,
  FormLotteryPrizeInputDTO,
} from "@/modules/forms/presentation/api/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const DRAW_MODE_LABELS: Record<"instant" | "deferred", string> = {
  instant: "ທັນທີ (ຕອນສົ່ງຟອມ)",
  deferred: "ຫຼັງ (ສຸ່ມໃນງານ)",
};

type PrizeDraft = {
  name: string;
  quantity: string;
  probabilityWeight: string;
  isConsolation: boolean;
  drawMode: "instant" | "deferred";
  redeemCodePrefix: string;
  enabled: boolean;
};

const EMPTY_DRAFT: PrizeDraft = {
  name: "",
  quantity: "1",
  probabilityWeight: "1",
  isConsolation: false,
  drawMode: "instant",
  redeemCodePrefix: "",
  enabled: true,
};

function draftFromPrize(prize: FormLotteryPrizeDTO): PrizeDraft {
  return {
    name: prize.name,
    quantity: String(prize.quantity),
    probabilityWeight: String(prize.probabilityWeight),
    isConsolation: prize.isConsolation,
    drawMode: prize.drawMode,
    redeemCodePrefix: prize.redeemCodePrefix ?? "",
    enabled: prize.enabled,
  };
}

function buildBody(draft: PrizeDraft): Omit<FormLotteryPrizeInputDTO, "sortOrder"> {
  const quantity = Number.parseInt(draft.quantity, 10);
  const weight = Number.parseInt(draft.probabilityWeight, 10);
  return {
    name: draft.name.trim(),
    quantity: draft.isConsolation ? 0 : quantity || 0,
    probabilityWeight: Number.isFinite(weight) ? weight : 1,
    isConsolation: draft.isConsolation,
    drawMode: draft.isConsolation ? "instant" : draft.drawMode,
    redeemCodePrefix: draft.redeemCodePrefix.trim() || null,
    enabled: draft.enabled,
  };
}

type FormLotteryPrizeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prize: FormLotteryPrizeDTO | null;
  sortOrder: number;
  isPending: boolean;
  onCreate: (body: FormLotteryPrizeInputDTO) => void;
  onUpdate: (
    prizeId: string,
    body: Partial<FormLotteryPrizeInputDTO>,
  ) => void;
};

export function FormLotteryPrizeDialog({
  open,
  onOpenChange,
  prize,
  sortOrder,
  isPending,
  onCreate,
  onUpdate,
}: FormLotteryPrizeDialogProps) {
  const isEdit = prize !== null;
  const [draft, setDraft] = useState<PrizeDraft>(EMPTY_DRAFT);

  const claimedCount = useMemo(() => {
    if (!prize || prize.isConsolation) return 0;
    return Math.max(0, prize.quantity - prize.remaining);
  }, [prize]);

  useEffect(() => {
    if (!open) return;
    setDraft(prize ? draftFromPrize(prize) : { ...EMPTY_DRAFT });
  }, [open, prize]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draft.name.trim()) return;
    const body = buildBody(draft);
    if (isEdit && prize) {
      onUpdate(prize.id, body);
    } else {
      onCreate({ ...body, sortOrder });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? "ແກ້ໄຂລາງວັນ" : "ເພີ່ມລາງວັນ"}</DialogTitle>
            <DialogDescription>
              {isEdit && claimedCount > 0
                ? `ແຈ້ງແລ້ວ ${claimedCount} ຊິ້ນ — ຈຳນວນລວມຕ້ອງບໍ່ຕ່ຳກວ່າ ${claimedCount}.`
                : "ຕັ້ງຈຳນວນ ນ້ຳໜັກ ແລະ ໂໝດການສຸ່ມ. ລາງວັນປົລອບໃຈບໍ່ມີໂຄດ ແລະ ບໍ່ຕັດສະຕັອກ."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="prize-dialog-name">ຊື່ລາງວັນ</Label>
              <Input
                id="prize-dialog-name"
                value={draft.name}
                autoFocus
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="prize-dialog-qty">ຈຳນວນ</Label>
                <Input
                  id="prize-dialog-qty"
                  type="number"
                  min={isEdit ? claimedCount : 0}
                  disabled={draft.isConsolation}
                  value={draft.quantity}
                  onChange={(e) =>
                    setDraft({ ...draft, quantity: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="prize-dialog-weight">ນ້ຳໜັກ</Label>
                <Input
                  id="prize-dialog-weight"
                  type="number"
                  min={0}
                  value={draft.probabilityWeight}
                  onChange={(e) =>
                    setDraft({ ...draft, probabilityWeight: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="prize-dialog-mode">ໂໝດການສຸ່ມ</Label>
              <SimpleSelect
                value={draft.drawMode}
                disabled={draft.isConsolation}
                onValueChange={(v) =>
                  setDraft({ ...draft, drawMode: v as "instant" | "deferred" })
                }
                options={[
                  { value: "instant", label: DRAW_MODE_LABELS.instant },
                  { value: "deferred", label: DRAW_MODE_LABELS.deferred },
                ]}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="prize-dialog-prefix">ຄຳນຳໜ້າໂຄດຮັບລາງວັນ</Label>
              <Input
                id="prize-dialog-prefix"
                disabled={draft.isConsolation}
                placeholder="LIHF-TW"
                value={draft.redeemCodePrefix}
                onChange={(e) =>
                  setDraft({ ...draft, redeemCodePrefix: e.target.value })
                }
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.isConsolation}
                disabled={isEdit && claimedCount > 0}
                onCheckedChange={(v) =>
                  setDraft({ ...draft, isConsolation: v === true })
                }
              />
              ລາງວັນປົລອບໃຈ (ບໍ່ຕັດສະຕັອກ, ບໍ່ມີໂຄດ)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.enabled}
                onCheckedChange={(v) =>
                  setDraft({ ...draft, enabled: v === true })
                }
              />
              ເປີດໃຊ້ລາງວັນນີ້ໃນການສຸ່ມ
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={isPending || !draft.name.trim()}>
              {isPending
                ? "ກຳລັງບັນທຶກ…"
                : isEdit
                  ? "ບັນທຶກການແກ້ໄຂ"
                  : "ເພີ່ມລາງວັນ"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
